import type { IntlShape } from 'react-intl'
import type { CharterBundleNights } from 'constants/charterFlight'
import { CHARTER_NIGHTS_TO_DURATION_DAYS, MAX_FNF_SEATS_LEFT_TO_SHOW_INVENTORY, MAX_ROOMS_LEFT_TO_SHOW_INVENTORY, MAX_SEATS_LEFT_TO_SHOW_INVENTORY } from 'constants/charterFlight'
import { fareClassToApiCabinMap, FlightsClassTypes, maxFlightChildAge, maxFlightInfantAge } from 'constants/flight'
import { getSumExtraGuestsSurcharge, allRoomsOccupantsFitRoomCapacity } from 'lib/checkout/cartReservationUtils'
import { isBundlePackageWithPrice } from 'lib/offer/offerTypes'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { useMemo } from 'react'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { min, sortBy, sum } from 'lib/array/arrayUtils'
import { getEffectiveFlightPrice, isFnfPriceActive } from 'lib/flights/flightUtils'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getLoyaltyEarnKey from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKey'
import type { AllocatedRoomPackage, BundleDateEntry, DateRange, ResolvedFlightInfo } from './types'

/**
 * Builds the hotel-portion loyalty earn calculation request for a Maldives charter bundle.
 * Uses the generic generator and falls back to a direct request for offer types the
 * generator's allow-list rejects (e.g. bundle/charter hotels mapped to `hotel_lpp_eps`).
 */
export function buildMaldivesCharterHotelLoyaltyCalculationRequest({
  offer,
  pkg,
  totalHotelPrice,
  totalHotelMemberPrice,
  currentRegionCode,
  nights,
}: {
  offer: App.Offer | App.OfferSummary
  pkg: App.HotelPackage
  totalHotelPrice: number
  totalHotelMemberPrice?: number
  currentRegionCode: string
  nights: number
}): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const request = generateLoyaltyPointsCalculatorHotelOptions(offer, {
    pkg,
    skip: false,
    currentRegionCode,
    price: totalHotelPrice,
    memberPrice: totalHotelMemberPrice,
    nights,
  })
  if (request) return request

  const fallbackProductType = getLoyaltyProductType(offer)
  if (!fallbackProductType) return undefined

  const propertyCountryCode = offer.property?.geoData?.countryCode
  const propertyState = offer.property?.geoData?.administrativeAreaLevel1
  const isUltraLux = !!offer.property?.isUltraLux
  let earnKey: string | undefined
  if (fallbackProductType === 'hotel_lpc') {
    earnKey = getLoyaltyEarnKey({ loyaltyProductType: fallbackProductType, options: { propertyCountryCode, propertyState, currentRegionCode, isUltraLux } })
  } else if (fallbackProductType === 'hotel_lpp_eps') {
    earnKey = getLoyaltyEarnKey({ loyaltyProductType: fallbackProductType, options: { propertyCountryCode, propertyState, currentRegionCode } })
  }
  return {
    price: totalHotelPrice,
    memberPrice: totalHotelMemberPrice ?? 0,
    loyaltyProductType: fallbackProductType,
    offerId: offer.id,
    packageId: pkg.id,
    earnKey,
    nights,
  }
}

/**
 * Extract the minimum seatsLeft from all flights in a journey
 * The inventory is stored per flight, so we need to find the minimum across all flights
 *
 * Data structure: result.onewayFares[i].fares[j].flightGroups[k].flights[m].seatsLeft
 */
export function getSeatsLeftFromJourney(journey?: App.JourneyV2): number | undefined {
  if (!journey?.flightGroup?.flights) {
    return undefined
  }

  const seatsLeftValues = journey.flightGroup.flights
    .map(flight => flight.seatsLeft)
    .filter((seats): seats is number => typeof seats === 'number' && seats >= 0)

  if (seatsLeftValues.length === 0) {
    return undefined
  }

  return Math.min(...seatsLeftValues)
}

export function getFnfSeatsLeftFromJourney(journey?: App.JourneyV2): number | undefined {
  if (!journey?.flightGroup?.flights) {
    return undefined
  }

  const seatsLeftValues = journey.flightGroup.flights
    .map(flight => flight.fnfSeatsLeft)
    .filter((seats): seats is number => typeof seats === 'number' && seats >= 0)

  if (seatsLeftValues.length === 0) {
    return undefined
  }

  return Math.min(...seatsLeftValues)
}

export function getPromotionSeatsLeftFromJourney(journey?: App.JourneyV2): number | undefined {
  if (!journey?.promotionPrice || !journey.flightGroup?.flights?.[0]?.promotionSeatsLeft) {
    return undefined
  }

  return journey.flightGroup.flights[0].promotionSeatsLeft
}

export function isRoundTripFnfPriceApplied(
  departingJourney?: App.JourneyV2,
  returningJourney?: App.JourneyV2,
  seatEligiblePassengers?: number,
): boolean {
  if (!departingJourney || !returningJourney) {
    return false
  }

  return isFnfPriceActive(departingJourney, seatEligiblePassengers) &&
    isFnfPriceActive(returningJourney, seatEligiblePassengers)
}

export function getJourneyTotalFareForFareClass({
  journey,
  fareClass,
  seatEligiblePassengers,
}: {
  journey?: App.JourneyV2
  fareClass: FlightsClassTypes
  seatEligiblePassengers?: number
}): number | undefined {
  if (!journey) {
    return undefined
  }
  const effectivePrice = getEffectiveFlightPrice(journey, seatEligiblePassengers)
  if (fareClass === FlightsClassTypes.ECONOMY) {
    return effectivePrice.all.totalFare
  }
  const cabin = fareClassToApiCabinMap[fareClass]
  const upsellFare = journey.upsellFares?.find(upsell => upsell.cabin === cabin)
  const upsellTotal = upsellFare?.cost?.all
  if (typeof upsellTotal === 'number' && upsellTotal > 0) {
    return upsellTotal
  }
  return effectivePrice.all.totalFare
}

export function getJourneyOriginalTotalFareForFareClass({
  journey,
  fareClass,
}: {
  journey?: App.JourneyV2
  fareClass: FlightsClassTypes
}): number | undefined {
  if (!journey) {
    return undefined
  }

  if (fareClass === FlightsClassTypes.ECONOMY) {
    return journey.price.all.totalFare
  }

  const cabin = fareClassToApiCabinMap[fareClass]
  const upsellFare = journey.upsellFares?.find(upsell => upsell.cabin === cabin)
  const upsellTotal = upsellFare?.cost?.all

  if (typeof upsellTotal === 'number' && upsellTotal > 0) {
    return upsellTotal
  }

  return journey.price.all.totalFare
}

/**
 * Resolve fare-class-specific flight price from flight data.
 * When journey data is available, extracts the price for the specified fare class
 * via upsell fares. Falls back to totalPrice when journey data is missing.
 */
export function resolveFlightPriceForFareClass(
  flightData: {
    totalPrice?: number
    departingJourney?: App.JourneyV2
    returningJourney?: App.JourneyV2
  } | undefined,
  fareClass: FlightsClassTypes,
  seatEligiblePassengers?: number,
): number | undefined {
  if (!flightData) {
    return undefined
  }

  if (!flightData.departingJourney || !flightData.returningJourney) {
    return flightData.totalPrice
  }
  const departingPrice = getJourneyTotalFareForFareClass({
    journey: flightData.departingJourney,
    fareClass,
    seatEligiblePassengers,
  })
  const returningPrice = getJourneyTotalFareForFareClass({
    journey: flightData.returningJourney,
    fareClass,
    seatEligiblePassengers,
  })
  if (departingPrice === undefined || returningPrice === undefined) {
    return flightData.totalPrice
  }
  // svc-flights only attaches feederEstimatedPrice on economy fares with non-MEL feeder
  // origin (CharterFeederPrices FDA). When absent, the field is undefined and contributes 0.
  const feederEstimatedTotal = (flightData.departingJourney.feederEstimatedPrice ?? 0)
    + (flightData.returningJourney.feederEstimatedPrice ?? 0)
  return departingPrice + returningPrice + feederEstimatedTotal
}

export function resolveOriginalFlightPriceForFareClass(
  flightData: {
    originalTotalPrice?: number
    departingJourney?: App.JourneyV2
    returningJourney?: App.JourneyV2
  } | undefined,
  fareClass: FlightsClassTypes,
): number | undefined {
  if (!flightData) {
    return undefined
  }

  if (!flightData.departingJourney || !flightData.returningJourney) {
    return flightData.originalTotalPrice
  }

  const departingPrice = getJourneyOriginalTotalFareForFareClass({
    journey: flightData.departingJourney,
    fareClass,
  })
  const returningPrice = getJourneyOriginalTotalFareForFareClass({
    journey: flightData.returningJourney,
    fareClass,
  })

  if (departingPrice === undefined || returningPrice === undefined) {
    return flightData.originalTotalPrice
  }

  // Mirror resolveFlightPriceForFareClass: include feederEstimatedPrice when present so the
  // calendar's strike-through original price stays consistent with the displayed total.
  const feederEstimatedTotal = (flightData.departingJourney.feederEstimatedPrice ?? 0)
    + (flightData.returningJourney.feederEstimatedPrice ?? 0)
  return departingPrice + returningPrice + feederEstimatedTotal
}

/**
 * Extract prices, fees, and roomsLeft from hotel offers
 *
 * This function processes hotel offers to extract:
 * - Lowest base price (regular and member prices) - matching useSearchPrices charterBundle logic
 * - All fees (propertyFees, surcharge, taxesAndFees, extraGuestSurcharges)
 * - Minimum roomsLeft across all offers
 *
 * Price calculation matches useSearchPrices charterBundle logic:
 * - basePrice = (package.price ?? 0) * numberOfRooms (for charterBundle, only base price is used)
 * - Fees are extracted separately and match useSearchPrices fees structure
 *
 * @param offers - Array of hotel offers with packages
 * @param nights - Number of nights to match package duration
 * @param numberOfRooms - Number of rooms to multiply price by (package.price is per room)
 * @param rooms - Array of occupancies for calculating extraGuestSurcharges
 * @param offerMetaData - Metadata containing availableRooms information
 * @returns Object containing lowestPrice, lowestMemberPrice, fees, and roomsLeft
 */
export function extractPricesAndRoomsLeftFromOffers(
  offers: Array<App.HotelOffer>,
  nights: number,
  numberOfRooms: number,
  rooms: Array<App.Occupants>,
  offerMetaData: Record<string, App.OfferListMetaData | undefined> | undefined,
): {
  lowestPrice: number | undefined
  lowestMemberPrice: number | undefined
  lowestPropertyFees: number | undefined
  lowestSurcharge: number | undefined
  lowestTaxesAndFees: number | undefined
  lowestExtraGuestSurcharges: number | undefined
  roomsLeft: number | undefined
} {
  const prices: Array<number> = []
  const memberPrices: Array<number> = []
  const propertyFeesValues: Array<number> = []
  const surchargeValues: Array<number> = []
  const taxesAndFeesValues: Array<number> = []
  const extraGuestSurchargesValues: Array<number> = []
  const roomsLeftValues: Array<number> = []

  offers.forEach(offer => {
    const matchingPackage = offer.packages.find(pkg => pkg.duration === nights)

    if (!matchingPackage) return

    // Get availableRooms from metaData (not from package)
    const metaData = offerMetaData?.[offer.id]

    // Check if offer is available in metadata
    // If metadata indicates unavailable, skip this offer
    if (metaData?.available === false) {
      return
    }

    // Check if package capacity fits the current room occupancy
    // This ensures we only use prices from packages that can accommodate the selected rooms
    // For example, if selecting 2 adults + 1 infant, we should only consider packages that support this occupancy
    const capacityValid = rooms.every(room =>
      allRoomsOccupantsFitRoomCapacity(
        room,
        matchingPackage.roomRate?.capacities ?? [],
        offer,
      ),
    )

    // Skip this package if capacity doesn't fit the current occupancy
    if (!capacityValid) {
      return
    }

    // Check if there are available rooms
    // Skip if availableRooms is 0 or undefined (when we have metadata)
    if (metaData?.availableRooms !== undefined && metaData.availableRooms === 0) {
      return
    }

    // Calculate extraGuestSurcharges (same logic as useSearchPrices)
    // For bundle packages, extraGuestSurcharges should be 0
    const extraGuestSurcharges = isBundlePackageWithPrice(matchingPackage)
      ? 0
      : getSumExtraGuestsSurcharge(rooms, offer, matchingPackage)

    // Calculate price multiplier (same as useSearchPrices: rate ? 1 : numberOfRooms)
    // Since we don't have rate in calendar item context, always use numberOfRooms
    const priceMultiplier = numberOfRooms

    // Extract base price only (matching useSearchPrices charterBundle logic)
    // For charterBundle, useSearchPrices uses: (actualPackage.price ?? 0) * charterBundlePriceMultiplier
    if (matchingPackage.price > 0) {
      const basePrice = matchingPackage.price * priceMultiplier
      prices.push(basePrice)

      // Extract all fees (matching useSearchPrices fees structure)
      const propertyFees = (matchingPackage.propertyFees ?? 0) * priceMultiplier
      const surcharge = (matchingPackage.surcharge ?? 0) * priceMultiplier
      const taxesAndFees = (matchingPackage.taxesAndFees ?? 0) * priceMultiplier

      propertyFeesValues.push(propertyFees)
      surchargeValues.push(surcharge)
      taxesAndFeesValues.push(taxesAndFees)
      extraGuestSurchargesValues.push(extraGuestSurcharges)

      // Get roomsLeft from metaData (OfferListMetaData.availableRooms)
      if (metaData?.availableRooms !== undefined && metaData.availableRooms > 0) {
        roomsLeftValues.push(metaData.availableRooms)
      }
    }

    // Extract member base price only
    if (matchingPackage.memberPrice && matchingPackage.memberPrice > 0) {
      const baseMemberPrice = matchingPackage.memberPrice * priceMultiplier
      memberPrices.push(baseMemberPrice)
    }
  })

  const lowestPrice = prices.length > 0 ? Math.min(...prices) : undefined
  const lowestMemberPrice = memberPrices.length > 0 ? Math.min(...memberPrices) : undefined
  // For fees, we'll use the minimum values (they should be similar across offers)
  // In practice, we might want to match fees with the offer that has the lowest price
  const lowestPropertyFees = propertyFeesValues.length > 0 ? Math.min(...propertyFeesValues) : undefined
  const lowestSurcharge = surchargeValues.length > 0 ? Math.min(...surchargeValues) : undefined
  const lowestTaxesAndFees = taxesAndFeesValues.length > 0 ? Math.min(...taxesAndFeesValues) : undefined
  const lowestExtraGuestSurcharges = extraGuestSurchargesValues.length > 0 ? Math.min(...extraGuestSurchargesValues) : undefined
  // Get minimum roomsLeft from all offers (similar to seatsLeft logic)
  const roomsLeft = roomsLeftValues.length > 0 ? Math.min(...roomsLeftValues) : undefined

  return {
    lowestPrice,
    lowestMemberPrice,
    lowestPropertyFees,
    lowestSurcharge,
    lowestTaxesAndFees,
    lowestExtraGuestSurcharges,
    roomsLeft,
  }
}

/**
 * Calculate bundle prices from hotel and flight data
 *
 * This function combines hotel prices with flight prices to calculate:
 * - Total bundle price (hotel + flight)
 * - Price per person
 * - Member prices (if available)
 *
 * @param params - Parameters containing hotel/flight data and passenger count
 * @returns CalendarItemBundleData with calculated bundle prices
 */
export function calculateBundlePrices(params: {
  hotelFetching: boolean
  flightFetching: boolean
  lowestPrice: number | undefined
  lowestMemberPrice: number | undefined
  totalFlightFare: number
  originalTotalFlightFare?: number
  totalPassengers: number
  seatEligiblePassengers: number
  seatsLeft: number | undefined
  promotionSeatsLeft?: number
  isPromotionPriceApplied?: boolean
  fnfSeatsLeft?: number
  isFnfPriceApplied?: boolean
  hasReturnFareFree?: boolean
  roomsLeft: number | undefined
  offerCount: number
}): {
  fetching: boolean
  available: boolean
  totalPrice: number
  pricePerPerson: number
  originalTotalPrice?: number
  originalPricePerPerson?: number
  memberTotalPrice: number | undefined
  memberPricePerPerson: number | undefined
  originalMemberTotalPrice?: number
  originalMemberPricePerPerson?: number
  hotelPrice: number | undefined
  hotelMemberPrice: number | undefined
  flightPrice: number
  offerCount: number
  seatsLeft: number | undefined
  promotionSeatsLeft?: number
  isPromotionPriceApplied?: boolean
  fnfSeatsLeft?: number
  isFnfPriceApplied?: boolean
  hasReturnFareFree?: boolean
  roomsLeft: number | undefined
} {
  const {
    hotelFetching,
    flightFetching,
    lowestPrice,
    lowestMemberPrice,
    totalFlightFare,
    originalTotalFlightFare,
    totalPassengers,
    seatEligiblePassengers,
    seatsLeft,
    promotionSeatsLeft,
    isPromotionPriceApplied,
    fnfSeatsLeft,
    isFnfPriceApplied,
    hasReturnFareFree,
    roomsLeft,
    offerCount,
  } = params

  // Calculate bundle prices
  const bothDataReady = !hotelFetching && !flightFetching && lowestPrice && totalFlightFare > 0

  let bundleTotalPrice: number | undefined
  let bundleMemberTotalPrice: number | undefined
  let bundlePricePerPerson: number | undefined
  let bundleMemberPricePerPerson: number | undefined
  let originalBundleTotalPrice: number | undefined
  let originalBundleMemberTotalPrice: number | undefined
  let originalBundlePricePerPerson: number | undefined
  let originalBundleMemberPricePerPerson: number | undefined

  if (bothDataReady) {
    bundleTotalPrice = lowestPrice + totalFlightFare
    bundlePricePerPerson = totalPassengers > 0 ? bundleTotalPrice / totalPassengers : undefined

    if (typeof originalTotalFlightFare === 'number' && originalTotalFlightFare > 0) {
      originalBundleTotalPrice = lowestPrice + originalTotalFlightFare
      originalBundlePricePerPerson = totalPassengers > 0 ? originalBundleTotalPrice / totalPassengers : undefined
    }

    if (lowestMemberPrice) {
      bundleMemberTotalPrice = lowestMemberPrice + totalFlightFare
      bundleMemberPricePerPerson = totalPassengers > 0 ? bundleMemberTotalPrice / totalPassengers : undefined

      if (typeof originalTotalFlightFare === 'number' && originalTotalFlightFare > 0) {
        originalBundleMemberTotalPrice = lowestMemberPrice + originalTotalFlightFare
        originalBundleMemberPricePerPerson = totalPassengers > 0
          ? originalBundleMemberTotalPrice / totalPassengers
          : undefined
      }
    }
  }

  // Seats must be sufficient for the selected number of seat-eligible passengers (exclude infants) when we have seat data
  const seatsSufficient = hasSufficientSeats(seatsLeft, seatEligiblePassengers)

  return {
    fetching: hotelFetching || flightFetching,
    available: !!bothDataReady && seatsSufficient,
    totalPrice: bundleTotalPrice ?? 0,
    pricePerPerson: bundlePricePerPerson ?? 0,
    originalTotalPrice: originalBundleTotalPrice,
    originalPricePerPerson: originalBundlePricePerPerson,
    memberTotalPrice: bundleMemberTotalPrice,
    memberPricePerPerson: bundleMemberPricePerPerson,
    originalMemberTotalPrice: originalBundleMemberTotalPrice,
    originalMemberPricePerPerson: originalBundleMemberPricePerPerson,
    hotelPrice: lowestPrice,
    hotelMemberPrice: lowestMemberPrice,
    flightPrice: totalFlightFare,
    offerCount,
    seatsLeft,
    promotionSeatsLeft,
    isPromotionPriceApplied,
    fnfSeatsLeft,
    isFnfPriceApplied,
    hasReturnFareFree,
    roomsLeft,
  }
}

/**
 * Returns true when seat inventory is sufficient for the given number of seat-eligible passengers.
 * - If seatsLeft is undefined, we optimistically assume sufficient (loading / not provided).
 */
export function hasSufficientSeats(seatsLeft: number | undefined, seatEligiblePassengers: number): boolean {
  return typeof seatsLeft === 'number' ? seatsLeft >= seatEligiblePassengers : true
}

const SEAT_ELIGIBLE_TYPES = new Set<App.OccupantType>(['adults', 'children', 'teenagers'])
const FLIGHT_AGE_OPTIONS = { maxInfantAge: maxFlightInfantAge, maxChildAge: maxFlightChildAge }

/**
 * Hook to calculate the number of seat-eligible passengers using flight age categorization.
 * Excludes infants (age 0-1) who travel as lap infants and don't need their own seat.
 */
export function useSeatEligiblePassengers(rooms: Array<App.Occupants>): number {
  return useMemo(
    () => sumUpOccupancies(rooms, SEAT_ELIGIBLE_TYPES, FLIGHT_AGE_OPTIONS),
    [rooms],
  )
}

/**
 * Calculates the inventory message based on seats and rooms availability.
 *
 * Logic:
 * 1. If both seats <= 10 AND rooms <= 5: prioritize showing seats
 * 2. If seats > 10 but rooms <= 5: show rooms
 * 3. If only seats are low (<= 10): show seats
 * 4. If only rooms are low (<= 5): show rooms
 *
 * @param seatsLeft - Number of seats left (undefined if not available)
 * @param roomsLeft - Number of rooms left (undefined if not available)
 * @returns Inventory message string, or empty string if no message should be shown
 */
export type InventoryMessageType = 'seat' | 'room' | 'bundle-sold-out' | 'hotel-sold-out' | 'none'

export function getInventoryMessageType(
  seatsLeft: number | undefined,
  roomsLeft: number | undefined,
): InventoryMessageType {
  const hasSeatsData = typeof seatsLeft === 'number'
  const hasRoomsData = typeof roomsLeft === 'number'

  // If either is 0, show unavailable
  if ((hasSeatsData && seatsLeft === 0)) {
    return 'bundle-sold-out'
  }
  if ((hasRoomsData && roomsLeft === 0)) {
    return 'hotel-sold-out'
  }

  const seatsLow =
    hasSeatsData &&
    seatsLeft > 0 &&
    seatsLeft <= MAX_SEATS_LEFT_TO_SHOW_INVENTORY

  const roomsLow =
    hasRoomsData &&
    roomsLeft > 0 &&
    roomsLeft <= MAX_ROOMS_LEFT_TO_SHOW_INVENTORY

  if (hasSeatsData && hasRoomsData) {
    if (seatsLow && roomsLow) {
      return 'seat'
    }
    if (!seatsLow && roomsLow) {
      return 'room'
    }
    if (seatsLow) {
      return 'seat'
    }
  } else if (seatsLow) {
    return 'seat'
  } else if (roomsLow) {
    return 'room'
  }

  return 'none'
}

export function getInventoryMessage(intl: IntlShape, seatsLeft: number | undefined, roomsLeft: number | undefined): string {
  const messageType = getInventoryMessageType(seatsLeft, roomsLeft)

  if (messageType === 'bundle-sold-out') {
    return intl.formatMessage({
      id: 'UbNyPnEgfdSzdknTFzT6',
      defaultMessage: 'Bundle sold out',
      description: 'Inventory message when all charter bundle seats are sold out',
    })
  }
  if (messageType === 'hotel-sold-out') {
    return intl.formatMessage({
      id: 'iYsZT1XoolqsOWujM6dv',
      defaultMessage: 'Hotel sold out',
      description: 'Inventory message when all hotel rooms are sold out',
    })
  }

  if (messageType === 'seat') {
    return intl.formatMessage(
      {
        id: '23YWa0_2ZjY7SjWiY2Ak',
        defaultMessage: '{count, plural, one {# seat left at this price} other {# seats left at this price}}',
        description: 'Urgency message showing limited seats remaining for charter flight',
      },
      { count: seatsLeft },
    )
  }

  if (messageType === 'room') {
    return intl.formatMessage(
      {
        id: 'UAOC3Gw-meNjAwKj3HaV',
        defaultMessage: '{count, plural, one {# room left at this price} other {# rooms left at this price}}',
        description: 'Urgency message showing limited hotel rooms remaining',
      },
      { count: roomsLeft },
    )
  }

  return ''
}

export function getFnfInventoryMessage(intl: IntlShape, fnfSeatsLeft: number | undefined): string {
  if (typeof fnfSeatsLeft !== 'number' || fnfSeatsLeft <= 0) {
    return ''
  }

  if (fnfSeatsLeft > MAX_FNF_SEATS_LEFT_TO_SHOW_INVENTORY) {
    return intl.formatMessage(
      {
        id: 'WEGDLZXG7dxAl3zgwI4S',
        defaultMessage: '{count}+ seats available',
        description: 'FnF inventory message shown when available seats exceed the display threshold and the count is capped with a plus sign',
      },
      { count: MAX_FNF_SEATS_LEFT_TO_SHOW_INVENTORY },
    )
  }

  return intl.formatMessage(
    {
      id: 'seEXhj0YG_LHyrz166Nx',
      defaultMessage: '{count, plural, one {# seat available} other {# seats available}}',
      description: 'FnF inventory message shown when one to ten seats are available at the displayed fare',
    },
    { count: fnfSeatsLeft },
  )
}

export function getPromotionInventoryMessage(intl: IntlShape, promotionSeatsLeft: number | undefined): string {
  if (!promotionSeatsLeft) {
    return ''
  }

  return intl.formatMessage(
    {
      id: '23YWa0_2ZjY7SjWiY2Ak',
      defaultMessage: '{count, plural, one {# seat left at this price} other {# seats left at this price}}',
      description: 'Urgency message showing limited seats remaining for charter flight',
    },
    { count: promotionSeatsLeft },
  )
}

export type CTAMessageType = 'hotel-sold-out' | 'bundle-sold-out' | 'none'

/**
 * Determines the CTA (Call to Action) message type based on inventory availability.
 *
 * @param seatsLeft - Number of seats left (undefined if not available)
 * @param roomsLeft - Number of rooms left (undefined if not available)
 * @returns CTA message type: 'hotel-sold-out', 'bundle-sold-out', or 'none'
 */
export function getCTAMessage(seatsLeft: number | undefined, roomsLeft: number | undefined): CTAMessageType {
  const hasSeatsData = typeof seatsLeft === 'number'
  const hasRoomsData = typeof roomsLeft === 'number'

  // Check if flights are sold out (seats = 0 or undefined)
  if ((hasSeatsData && seatsLeft === 0)) {
    return 'bundle-sold-out'
  }

  // Check if hotel is sold out (rooms = 0 or undefined)
  if ((hasRoomsData && roomsLeft === 0)) {
    return 'hotel-sold-out'
  }

  return 'none'
}

/**
 * Calculate the lowest available hotel price from a list of offer IDs
 *
 * This function filters offers based on availability and capacity constraints:
 * - Checks if offer is available in metadata
 * - Verifies package capacity fits the current occupancy
 * - Ensures there are available rooms
 * - Returns the lowest price (member price if luxPlusEnabled, otherwise regular price)
 *
 * @param params - Parameters for price calculation
 * @param params.offerIds - Array of offer IDs to process
 * @param params.allOffers - Map of all offers by offer ID
 * @param params.offerMetaData - Map of offer metadata by offer ID
 * @param params.rooms - Array of occupancies to check capacity against
 * @param params.nights - Number of nights to match package duration
 * @param params.luxPlusEnabled - Whether to prefer member prices
 * @returns Lowest available hotel price, or 0 if no valid price found
 */
export function getLowestAvailableHotelPrice(params: {
  offerIds: Array<string>
  allOffers: Record<string, App.Offer>
  offerMetaData: Record<string, App.OfferListMetaData | undefined> | undefined
  rooms: Array<App.Occupants>
  nights: number
  luxPlusEnabled: boolean
}): number {
  const { offerIds, allOffers, offerMetaData, rooms, nights, luxPlusEnabled } = params

  const hotelPrices = offerIds
    .map(offerId => {
      const offer = allOffers[offerId] as App.HotelOffer | undefined
      if (!offer) return null

      // Check if offer is available in metadata
      const metaData = offerMetaData?.[offerId]
      if (metaData?.available === false) {
        return null
      }

      const pkg = offer.packages.find(p => p.duration === nights)
      if (!pkg) return null

      // Check if package capacity fits the current occupancy
      // Skip if any room doesn't fit the package capacity
      const capacityValid = rooms.every(room =>
        allRoomsOccupantsFitRoomCapacity(
          room,
          pkg.roomRate?.capacities ?? [],
          offer,
        ),
      )
      if (!capacityValid) {
        return null
      }

      // Check if there are available rooms
      if (metaData?.availableRooms !== undefined && metaData.availableRooms === 0) {
        return null
      }

      // Only include price if package has valid price
      if (pkg.price > 0) {
        return luxPlusEnabled ? (pkg.memberPrice || pkg.price) : pkg.price
      }

      return null
    })
    .filter((p): p is number => typeof p === 'number' && p > 0)

  return hotelPrices.length > 0 ? Math.min(...hotelPrices) : 0
}

/**
 * Filter dateRangesByMonth by selected nights/duration
 *
 * Note: We filter by durationDays instead of nights because durationDays represents the total trip duration
 * which matches the user's selection (5 nights = ~7 days, 12 nights = ~14 days)
 *
 * @param allDateRangesByMonth - All date ranges grouped by month from charter flight journeys API
 * @param nights - Selected bundle nights (5 or 12)
 * @returns Filtered dateRangesByMonth containing only ranges matching the expected duration
 */
export function filterDateRangesByNights(
  allDateRangesByMonth: Record<string, Array<DateRange>>,
  nights: number,
): Record<string, Array<DateRange>> {
  const expectedDurationDays = CHARTER_NIGHTS_TO_DURATION_DAYS[nights as CharterBundleNights] ?? nights

  const filtered: Record<string, Array<DateRange>> = {}

  Object.entries(allDateRangesByMonth).forEach(([month, ranges]) => {
    // Filter by durationDays which represents the total trip duration
    const filteredRanges = ranges.filter(range => range.durationDays === expectedDurationDays)
    if (filteredRanges.length > 0) {
      filtered[month] = filteredRanges
    }
  })

  return filtered
}

interface PackageAllocationResult {
  allocatedPackages: Array<AllocatedRoomPackage>
  totalHotelPrice: number
  totalHotelMemberPrice?: number
  totalHotelValue?: number
  roomBreakdown: Array<{ name: string; count: number; size?: string }>
  isValid: boolean
  minRoomsLeft?: number
}

/**
 * Allocate packages to rooms for a specific date.
 *
 * For each room, finds valid packages from the calendar, then allocates the
 * cheapest available package while respecting room inventory limits.
 * Used by both the search page and offer page charter bundle pricing.
 */
export function allocatePackagesForDate(params: {
  offer: App.HotelOffer | App.HotelOfferSummary
  rooms: Array<App.Occupants>
  durationPackages: Array<App.HotelPackage>
  calendarDataByOccupancy: Record<string, Record<string, App.Calendar>>
  checkIn: string
}): PackageAllocationResult {
  const { offer, rooms, durationPackages, calendarDataByOccupancy, checkIn } = params

  const emptyResult: PackageAllocationResult = {
    allocatedPackages: [],
    totalHotelPrice: 0,
    totalHotelMemberPrice: undefined,
    totalHotelValue: undefined,
    roomBreakdown: [],
    isValid: false,
  }

  if (rooms.length === 0 || durationPackages.length === 0) {
    return emptyResult
  }

  type PackageWithDatePricing = {
    package: App.HotelPackage
    hotelPrice: number
    hotelMemberPrice?: number
    hotelValue?: number
    availableRooms?: number
  }

  const validPackagesByRoom = rooms.map((room, index) => {
    const occupancyKey = generateOccupancyStringByRoom(room)
    const occupancyCalendars = calendarDataByOccupancy[occupancyKey]

    const validPackages: Array<PackageWithDatePricing> = []

    for (const pkg of durationPackages) {
      const capacities = pkg.roomRate?.capacities ?? []
      if (!allRoomsOccupantsFitRoomCapacity(room, capacities, offer)) {
        continue
      }

      const calendar = occupancyCalendars?.[pkg.uniqueKey]
      if (!calendar) {
        continue
      }

      let dayData: App.CalendarDay | undefined
      for (const month of calendar.months) {
        dayData = month.days.find(d => d.checkIn === checkIn)
        if (dayData) break
      }

      if (!dayData?.hotelAvailable) {
        continue
      }

      validPackages.push({
        package: pkg,
        hotelPrice: dayData.hotelTotal,
        hotelMemberPrice: dayData.hotelMemberTotal || undefined,
        hotelValue: dayData.hotelValue,
        availableRooms: dayData.availableRooms,
      })
    }

    return { roomIndex: index, room, validPackages }
  })

  const getTotalOccupancy = (room: App.Occupants) =>
    room.adults + (room.teenagers ?? 0) + (room.children ?? 0) + (room.infants ?? 0)

  const sortedRooms = sortBy(
    validPackagesByRoom,
    item => getTotalOccupancy(item.room),
    'desc',
  )

  const allocatedPackages: Array<AllocatedRoomPackage> = []
  const usedInventory: Record<string, number> = {}

  for (const roomData of sortedRooms) {
    const availablePackages = roomData.validPackages.filter(pkg => {
      const used = usedInventory[pkg.package.uniqueKey] ?? 0
      const available = pkg.availableRooms ?? Infinity
      return available - used > 0
    })

    if (availablePackages.length === 0) {
      return emptyResult
    }

    const cheapest = min(availablePackages, pkg => pkg.hotelPrice)
    if (!cheapest) {
      return emptyResult
    }

    usedInventory[cheapest.package.uniqueKey] = (usedInventory[cheapest.package.uniqueKey] ?? 0) + 1

    allocatedPackages.push({
      roomIndex: roomData.roomIndex,
      room: roomData.room,
      package: cheapest.package,
      hotelPrice: cheapest.hotelPrice,
      hotelMemberPrice: cheapest.hotelMemberPrice,
      hotelValue: cheapest.hotelValue,
      availableRooms: cheapest.availableRooms,
    })
  }

  allocatedPackages.sort((a, b) => a.roomIndex - b.roomIndex)

  const totalHotelPrice = sum(allocatedPackages, pkg => pkg.hotelPrice)
  const allHaveMemberPrice = allocatedPackages.every(pkg => pkg.hotelMemberPrice !== undefined)
  const totalHotelMemberPrice = allHaveMemberPrice
    ? sum(allocatedPackages, pkg => pkg.hotelMemberPrice ?? 0)
    : undefined
  const allHaveValue = allocatedPackages.every(pkg => pkg.hotelValue !== undefined)
  const totalHotelValue = allHaveValue
    ? sum(allocatedPackages, pkg => pkg.hotelValue ?? 0)
    : undefined

  const packageCounts: Record<string, { name: string; count: number; size?: string }> = {}
  for (const allocated of allocatedPackages) {
    const roomType = allocated.package.roomType
    if (roomType) {
      const key = roomType.id
      if (!packageCounts[key]) {
        packageCounts[key] = {
          name: roomType.name,
          count: 0,
          size: roomType.sizeSqm ? `${roomType.sizeSqm}m²` : undefined,
        }
      }
      packageCounts[key].count += 1
    }
  }

  const roomBreakdown = Object.values(packageCounts).map(item => ({
    name: `${item.count}x ${item.name}`,
    count: item.count,
    size: item.size,
  }))

  const minRoomsLeft = allocatedPackages.reduce((currentMin, pkg) => {
    const available = pkg.availableRooms
    if (available === undefined) return currentMin
    return currentMin === undefined ? available : Math.min(currentMin, available)
  }, undefined as number | undefined)

  return {
    allocatedPackages,
    totalHotelPrice,
    totalHotelMemberPrice,
    totalHotelValue,
    roomBreakdown,
    isValid: allocatedPackages.length === rooms.length,
    minRoomsLeft,
  }
}

/**
 * Compute bundle pricing data for a single offer across all provided dates.
 *
 * This is the shared core that both the search page (useMaldivesOffersPricing)
 * and the offer page (useOfferCharterCalendarData) use. Callers resolve
 * flight prices in their own way before passing them here.
 */
export function computeBundleDataForOffer(params: {
  dateRanges: Array<{ checkIn: string }>
  offer: App.HotelOffer | App.HotelOfferSummary
  rooms: Array<App.Occupants>
  durationPackages: Array<App.HotelPackage>
  calendarDataByOccupancy: Record<string, Record<string, App.Calendar>>
  resolvedFlightByDate: Record<string, ResolvedFlightInfo>
  totalPassengers: number
  seatEligiblePassengers: number
  isCalendarLoading: boolean
  hasNoPackage?: boolean
  /** Region code for the hotel-portion loyalty earn calculation request — used by
   *  {@link generateLoyaltyPointsCalculatorHotelOptions} to derive the earn key. Optional so
   *  legacy callers without geo context still work; cells just won't render hotel earn. */
  currentRegionCode?: string
}): Record<string, BundleDateEntry> {
  const {
    dateRanges,
    offer,
    rooms,
    durationPackages,
    calendarDataByOccupancy,
    resolvedFlightByDate,
    totalPassengers,
    seatEligiblePassengers,
    isCalendarLoading,
    hasNoPackage = false,
    currentRegionCode,
  } = params

  const result: Record<string, BundleDateEntry> = {}

  for (const { checkIn } of dateRanges) {
    const flight = resolvedFlightByDate[checkIn]
    const flightPrice = flight?.price
    const originalFlightPrice = flight?.originalPrice
    const isFetching = isCalendarLoading || (flight?.fetching ?? false)
    const seatsLeft = flight?.seatsLeft ?? (isFetching ? undefined : 0)
    const promotionSeatsLeft = flight?.promotionSeatsLeft
    const isPromotionPriceApplied = flight?.isPromotionPriceApplied
    const fnfSeatsLeft = flight?.fnfSeatsLeft
    const isFnfPriceApplied = flight?.isFnfPriceApplied
    const hasReturnFareFree = flight?.hasReturnFareFree
    const seatsSufficient = hasSufficientSeats(seatsLeft, seatEligiblePassengers)

    if (!flightPrice) {
      result[checkIn] = {
        bundleData: {
          fetching: isFetching,
          available: false,
          totalPrice: 0,
          pricePerPerson: 0,
          offerCount: 0,
          seatsLeft,
          promotionSeatsLeft,
          isPromotionPriceApplied,
          fnfSeatsLeft,
          isFnfPriceApplied,
          hasReturnFareFree,
          hasNoPackage,
        },
        allocatedPackages: [],
        roomBreakdown: [],
      }
      continue
    }

    const allocation = allocatePackagesForDate({
      offer,
      rooms,
      durationPackages,
      calendarDataByOccupancy,
      checkIn,
    })

    if (!allocation.isValid) {
      result[checkIn] = {
        bundleData: {
          fetching: isFetching,
          available: false,
          totalPrice: 0,
          pricePerPerson: 0,
          offerCount: 0,
          seatsLeft,
          promotionSeatsLeft,
          isPromotionPriceApplied,
          hasReturnFareFree,
          hasNoPackage,
        },
        allocatedPackages: [],
        roomBreakdown: [],
      }
      continue
    }

    const bundleTotalPrice = allocation.totalHotelPrice + flightPrice
    const bundlePricePerPerson = totalPassengers > 0 ? bundleTotalPrice / totalPassengers : 0
    const originalBundleTotalPrice = originalFlightPrice !== undefined
      ? allocation.totalHotelPrice + originalFlightPrice
      : undefined
    const originalBundlePricePerPerson = originalBundleTotalPrice !== undefined && totalPassengers > 0
      ? originalBundleTotalPrice / totalPassengers
      : undefined

    const bundleMemberTotalPrice = allocation.totalHotelMemberPrice
      ? allocation.totalHotelMemberPrice + flightPrice
      : undefined
    const bundleMemberPricePerPerson = bundleMemberTotalPrice && totalPassengers > 0
      ? bundleMemberTotalPrice / totalPassengers
      : undefined
    const originalBundleMemberTotalPrice = allocation.totalHotelMemberPrice !== undefined && originalFlightPrice !== undefined
      ? allocation.totalHotelMemberPrice + originalFlightPrice
      : undefined
    const originalBundleMemberPricePerPerson = originalBundleMemberTotalPrice !== undefined && totalPassengers > 0
      ? originalBundleMemberTotalPrice / totalPassengers
      : undefined

    const firstAllocatedPackage = allocation.allocatedPackages[0]?.package
    const hotelLoyaltyCalculationRequest = firstAllocatedPackage && currentRegionCode
      ? buildMaldivesCharterHotelLoyaltyCalculationRequest({
          offer,
          pkg: firstAllocatedPackage,
          totalHotelPrice: allocation.totalHotelPrice,
          totalHotelMemberPrice: allocation.totalHotelMemberPrice,
          currentRegionCode,
          nights: firstAllocatedPackage.duration,
        })
      : undefined

    result[checkIn] = {
      bundleData: {
        fetching: isFetching,
        available: seatsSufficient,
        totalPrice: bundleTotalPrice,
        pricePerPerson: bundlePricePerPerson,
        originalTotalPrice: originalBundleTotalPrice,
        originalPricePerPerson: originalBundlePricePerPerson,
        memberTotalPrice: bundleMemberTotalPrice,
        memberPricePerPerson: bundleMemberPricePerPerson,
        originalMemberTotalPrice: originalBundleMemberTotalPrice,
        originalMemberPricePerPerson: originalBundleMemberPricePerPerson,
        hotelPrice: allocation.totalHotelPrice,
        hotelMemberPrice: allocation.totalHotelMemberPrice,
        hotelValue: allocation.totalHotelValue,
        flightPrice,
        offerCount: 1,
        seatsLeft,
        promotionSeatsLeft,
        isPromotionPriceApplied,
        fnfSeatsLeft,
        isFnfPriceApplied,
        hasReturnFareFree,
        roomsLeft: allocation.minRoomsLeft,
        hasNoPackage,
        hotelLoyaltyCalculationRequest,
      },
      allocatedPackages: allocation.allocatedPackages,
      roomBreakdown: allocation.roomBreakdown,
    }
  }

  return result
}
